import { Alert, AnimatedContentLoader, Badge, Button, Prompt, Select } from '@components'
import { getSettingsPageUrl } from '@root/constants'
import { useBusinessContext } from '@root/context'
import {
  IReferralReward,
  referralMyLinkQueryKey,
  referralRewardsQueryKey,
  useRedeemReferralReward,
  useReferralMyLink,
  useReferralRedeemEligibleBusinesses,
  useReferralRewards
} from '@query'
import { isHttpError } from '@query/root'
import { getApiErrorMessage } from '@utils'
import { useQueryClient } from '@tanstack/react-query'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaGift, FaUserFriends } from 'react-icons/fa'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import styled from 'styled-components'

type RewardGroup = {
  role: IReferralReward['reward_role']
  rewards: IReferralReward[]
}

export const ReferralRewardsSection: React.FC = () => {
  const [t] = useTranslation()
  const { slug: contextSlug } = useBusinessContext()
  const {
    data: rewards,
    isLoading: isRewardsLoading,
    isError: isRewardsError,
    refetch,
    isFetching
  } = useReferralRewards()
  const { data: referralLink } = useReferralMyLink()
  const {
    eligibleBusinesses,
    selectedBusiness,
    selectedBusinessSlug,
    setSelectedBusinessSlug,
    isLoading: isEligibleBusinessesLoading
  } = useReferralRedeemEligibleBusinesses()
  const { redeemReferralReward, isRedeemingReferralReward } = useRedeemReferralReward()
  const queryClient = useQueryClient()
  const [redeemingRewardId, setRedeemingRewardId] = useState<number | null>(null)
  const [confirmRewardId, setConfirmRewardId] = useState<number | null>(null)
  const [redeemErrorMessage, setRedeemErrorMessage] = useState<string | null>(null)

  const isLoading = isRewardsLoading || isEligibleBusinessesLoading
  const hasRewards = Boolean(rewards && rewards.length > 0)
  const redeemedCount = referralLink?.redeemed_count ?? 0
  const showRewardsUsed = !hasRewards && redeemedCount > 0
  const showBusinessPicker = hasRewards && eligibleBusinesses.length > 1
  const businessName = selectedBusiness?.name ?? selectedBusinessSlug ?? contextSlug

  const rewardGroups = useMemo<RewardGroup[]>(() => {
    if (!rewards?.length) {
      return []
    }

    const referrerRewards = rewards.filter(reward => reward.reward_role === 'referrer')
    const refereeRewards = rewards.filter(reward => reward.reward_role === 'referee')

    return [
      ...(referrerRewards.length > 0
        ? [{ role: 'referrer' as const, rewards: referrerRewards }]
        : []),
      ...(refereeRewards.length > 0 ? [{ role: 'referee' as const, rewards: refereeRewards }] : [])
    ]
  }, [rewards])

  const closeConfirmPrompt = () => setConfirmRewardId(null)

  const handleRedeem = async (rewardId: number): Promise<boolean> => {
    if (!selectedBusinessSlug) {
      return false
    }

    setRedeemingRewardId(rewardId)
    setRedeemErrorMessage(null)
    try {
      await redeemReferralReward({
        reward_id: rewardId,
        business_slug: selectedBusinessSlug
      })
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: referralRewardsQueryKey }),
        queryClient.invalidateQueries({ queryKey: referralMyLinkQueryKey })
      ])
      toast.success(t('settings.referral.redeem.success'))
      return true
    } catch (error) {
      const errorPayload = isHttpError(error) ? error.data : undefined
      const errorCode =
        errorPayload &&
        typeof errorPayload === 'object' &&
        typeof (errorPayload as { code?: string }).code === 'string'
          ? (errorPayload as { code: string }).code
          : undefined
      const fallbackMessage =
        errorCode === 'plan_item_not_found'
          ? t('settings.referral.redeem.planItemNotFound')
          : t('settings.referral.redeem.error')
      const message = getApiErrorMessage(error, fallbackMessage)
      setRedeemErrorMessage(message)
      toast.error(message)
      return false
    } finally {
      setRedeemingRewardId(null)
    }
  }

  const getRewardPrimaryTitle = (reward: IReferralReward) => {
    if (reward.reward_role === 'referee') {
      return t('settings.referral.redeem.refereeRewardTitle')
    }
    return reward.counterpart_name
  }

  const getRoleBadgeLabel = (role: IReferralReward['reward_role']) =>
    role === 'referrer'
      ? t('settings.referral.redeem.invitedFriendBadge')
      : t('settings.referral.redeem.joinedWithReferralBadge')

  const getSectionTitle = (role: IReferralReward['reward_role']) =>
    role === 'referrer'
      ? t('settings.referral.redeem.creditsFromInviting')
      : t('settings.referral.redeem.creditFromSignup')

  const getSectionIcon = (role: IReferralReward['reward_role']) =>
    role === 'referrer' ? <FaUserFriends /> : <FaGift />

  const renderRewardCard = (reward: IReferralReward) => (
    <RewardItem key={reward.reward_id}>
      <RewardContent>
        <RewardMeta>
          <Badge type="default">{getRoleBadgeLabel(reward.reward_role)}</Badge>
          <Badge type="info">{t('settings.referral.redeem.oneMonth')}</Badge>
        </RewardMeta>
        <RewardTitle>{getRewardPrimaryTitle(reward)}</RewardTitle>
      </RewardContent>
      <RewardAction>
        <Button
          size="sm"
          showSpinner={isRedeemingReferralReward && redeemingRewardId === reward.reward_id}
          disabled={isRedeemingReferralReward || !selectedBusinessSlug}
          onClick={() => setConfirmRewardId(reward.reward_id)}
        >
          {t('settings.referral.redeem.applyCredit')}
        </Button>
      </RewardAction>
    </RewardItem>
  )

  const renderRewardList = () => {
    if (!rewardGroups.length) {
      return null
    }

    return (
      <RewardsContainer>
        {rewardGroups.map(group => (
          <RewardGroup key={group.role}>
            <GroupHeader>
              <GroupIcon>{getSectionIcon(group.role)}</GroupIcon>
              <GroupTitle>{getSectionTitle(group.role)}</GroupTitle>
            </GroupHeader>
            <RewardList>{group.rewards.map(reward => renderRewardCard(reward))}</RewardList>
          </RewardGroup>
        ))}
      </RewardsContainer>
    )
  }

  return (
    <>
      <AnimatedContentLoader isLoading={isLoading}>
        {isRewardsError ? (
          <ErrorState>
            <Alert type="error" description={t('settings.referral.redeem.loadError')} />
            <Button showSpinner={isFetching} onClick={() => refetch()}>
              {t('general.retry')}
            </Button>
          </ErrorState>
        ) : showRewardsUsed ? (
          <Alert
            type="info"
            description={t('settings.referral.redeem.rewardsUsed', { count: redeemedCount })}
          />
        ) : !hasRewards ? (
          <Alert type="info" description={t('settings.referral.redeem.noRewards')} />
        ) : eligibleBusinesses.length === 0 ? (
          <>
            <Alert
              type="warning"
              description={t('settings.referral.redeem.noEligibleBusinesses')}
            />
            <PlanLink to={getSettingsPageUrl(contextSlug, { page: 'plan' })}>
              {t('settings.referral.redeem.upgradePlanLink')}
            </PlanLink>
          </>
        ) : (
          <RedeemContent>
            {redeemErrorMessage && (
              <Alert type="error" marginBottom={0} description={redeemErrorMessage} />
            )}
            <Alert
              marginBottom={0}
              description={t(
                showBusinessPicker
                  ? 'settings.referral.redeem.descriptionWithBusinessPicker'
                  : 'settings.referral.redeem.description',
                { businessName }
              )}
            />

            {showBusinessPicker && (
              <Select
                label={t('settings.referral.redeem.businessLabel')}
                name="referral-redeem-business"
                value={selectedBusinessSlug ?? ''}
                onChange={event => {
                  setSelectedBusinessSlug(event.target.value)
                  setRedeemErrorMessage(null)
                }}
              >
                {eligibleBusinesses.map(business => (
                  <option key={business.slug} value={business.slug}>
                    {business.name}
                  </option>
                ))}
              </Select>
            )}

            {renderRewardList()}
          </RedeemContent>
        )}
      </AnimatedContentLoader>

      <Prompt
        isVisible={confirmRewardId != null}
        onClose={closeConfirmPrompt}
        title={t('settings.referral.redeem.confirmTitle')}
        description={t('settings.referral.redeem.confirmDescription', { businessName })}
        buttons={[
          {
            text: t('general.cancel'),
            intent: 'default',
            action: closeConfirmPrompt,
            disabled: isRedeemingReferralReward
          },
          {
            text: t('settings.referral.redeem.applyCredit'),
            intent: 'success',
            showSpinner: isRedeemingReferralReward,
            disabled: isRedeemingReferralReward || !selectedBusinessSlug,
            action: async () => {
              if (confirmRewardId == null) {
                return
              }
              const success = await handleRedeem(confirmRewardId)
              if (success) {
                closeConfirmPrompt()
              }
            }
          }
        ]}
      />
    </>
  )
}

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  min-width: 0;
`

const RedeemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg}rem;
  min-width: 0;
`

const RewardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xl}rem;
`

const RewardGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const GroupIcon = styled.span`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.metalGray};

  svg {
    width: ${({ theme }) => theme.fontSize.md}rem;
    height: ${({ theme }) => theme.fontSize.md}rem;
  }
`

const GroupTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutralBlack};
  overflow-wrap: anywhere;
`

const RewardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const RewardItem = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
  border-radius: 0.6rem;
  background: ${({ theme }) => theme.colors.neutralGray};

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`

const RewardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  min-width: 0;
  flex: 1 1 12rem;
`

const RewardAction = styled.div`
  flex: 0 0 auto;

  @media (max-width: 768px) {
    width: 100%;

    button {
      width: 100%;
    }
  }
`

const RewardMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const RewardTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutralBlack};
  overflow-wrap: anywhere;
`

const PlanLink = styled(Link)`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  width: fit-content;

  &:hover {
    text-decoration: underline;
  }
`
